import React from 'react';

import { DataState, deriveDataState } from '../loading';
import ItemsTable from './ItemsTable';
import ItemCardsGrid from './ItemCardsGrid';
import ItemsListSkeleton from './ItemsListSkeleton';

/**
 * One body for any list of items: the collection list and a related list
 * render their results the same way (docs/platform-standards.md §14).
 *
 *   <ItemsListBody
 *     view='table' | 'grid'
 *     items={items}
 *     loading={loading}
 *     error={error}
 *     onRetry={reload}
 *     empty={<p>Nothing linked yet</p>}
 *     {...listProps}          // forwarded to the table or the grid
 *   />
 *
 * - loading with no rows yet: the skeleton, shaped like the view.
 * - loading with rows on screen: refreshing; the rows stay.
 * - error: what failed and Retry; rows already on screen stay under it.
 * - no rows, no error: the caller's `empty` node.
 *
 * `children`, when given, replaces the table or grid (the collection list
 * draws grouped grids and worklists of its own) but keeps the state handling.
 */
export default function ItemsListBody({
	view = 'table',
	items = [],
	loading = false,
	error = null,
	onRetry,
	empty = null,
	label = 'items',
	children,
	...listProps
}) {
	const rows = items || [];
	const hasData = rows.length > 0;
	// A caller that draws its own body and gives no empty node (the collection
	// list, whose empty states sit above this) keeps drawing that body.
	const ownsEmpty = empty !== null || children === undefined || children === null;
	const isEmpty = ownsEmpty && !loading && !error && !hasData;
	const state = deriveDataState({ loading, error, hasData, isEmpty });

	let body = children;
	if (body === undefined || body === null) {
		// DataState owns waiting, so the list does not draw its own spinner.
		body = view === 'table'
			? <ItemsTable {...listProps} items={rows} loading={false} />
			: <ItemCardsGrid {...listProps} items={rows} loading={false} emptyText={null} />;
	}

	return (
		<DataState
			state={state}
			hasData={hasData}
			isEmpty={isEmpty}
			skeleton={<ItemsListSkeleton variant={view === 'table' ? 'table' : 'tiles'} />}
			empty={empty}
			error={error}
			onRetry={onRetry}
			label={label}
		>
			{body}
		</DataState>
	);
}
